import React, { useMemo, useState } from "react";
import { getStatusColor, initialsFromName } from "../core/btw-status-colors";

const relativeTime = (dateLike) => {
  if (!dateLike) return "";
  const t = new Date(dateLike).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Date.now() - t;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "zojuist";
  if (min < 60) return `${min}m geleden`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}u geleden`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d geleden`;
  return new Date(dateLike).toLocaleDateString("nl-NL");
};

const exactTime = (dateLike) => {
  if (!dateLike) return "";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("nl-NL");
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
};

const dayLabel = (dateLike) => {
  const today = startOfDay(new Date());
  const day = startOfDay(dateLike);
  const diff = Math.round((today - day) / 86_400_000);
  if (diff === 0) return "Vandaag";
  if (diff === 1) return "Gisteren";
  if (diff < 7) {
    return new Date(dateLike).toLocaleDateString("nl-NL", {
      weekday: "long",
    });
  }
  return new Date(dateLike).toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "long",
    year:
      new Date(dateLike).getFullYear() === new Date().getFullYear()
        ? undefined
        : "numeric",
  });
};

const AVATAR_PALETTE = [
  "bg-brand",
  "bg-sky-500",
  "bg-violet-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-indigo-500",
  "bg-teal-500",
  "bg-fuchsia-500",
];
const colorForName = (name = "") => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
};

const Avatar = ({ name, system, photoUrl }) => {
  if (system) {
    return (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#091E420F] text-ink-subtlest ring-2 ring-white">
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M11.49 3.17a1 1 0 00-2 0l-.36 1.45a6 6 0 00-1.62.94L6.1 4.83a1 1 0 00-1.27 1.27l.73 1.41a6 6 0 00-.94 1.62L3.17 9.5a1 1 0 000 2l1.45.36c.24.6.55 1.14.94 1.62l-.73 1.41a1 1 0 001.27 1.27l1.41-.73c.48.39 1.02.7 1.62.94l.36 1.45a1 1 0 002 0l.36-1.45a6 6 0 001.62-.94l1.41.73a1 1 0 001.27-1.27l-.73-1.41c.39-.48.7-1.02.94-1.62l1.45-.36a1 1 0 000-2l-1.45-.36a6 6 0 00-.94-1.62l.73-1.41a1 1 0 00-1.27-1.27l-1.41.73a6 6 0 00-1.62-.94l-.36-1.45zM10 13a3 3 0 110-6 3 3 0 010 6z"
            clipRule="evenodd"
          />
        </svg>
      </span>
    );
  }
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name}
        title={name}
        className="h-6 w-6 rounded-full object-cover ring-2 ring-white"
      />
    );
  }
  return (
    <span
      title={name}
      className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white ring-2 ring-white ${colorForName(
        name,
      )}`}
    >
      {initialsFromName(name)}
    </span>
  );
};

const StatusBadge = ({ name, statusesById }) => {
  const cfg = statusesById?.[name];
  const colors = getStatusColor(cfg?.color);
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-px text-[11px] font-bold uppercase tracking-wide ${colors.pill}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
      {name ?? "-"}
    </span>
  );
};

const ChangeLine = ({ activity, statusesById }) => {
  const meta = activity.meta ?? {};
  if (activity.type === "status_change") {
    return (
      <span className="text-ink-subtle">
        wijzigde status{" "}
        <StatusBadge
          name={meta.fromStatusName}
          statusesById={statusesById}
        />{" "}
        →{" "}
        <StatusBadge name={meta.toStatusName} statusesById={statusesById} />
      </span>
    );
  }
  if (activity.type === "assignment_change") {
    return (
      <span className="text-ink-subtle">
        wees toe aan{" "}
        <span className="rounded bg-[#091E420F] px-1.5 py-0.5 text-[11px] font-medium text-ink">
          {meta.toAssigneeName ?? "Niemand"}
        </span>
      </span>
    );
  }
  if (activity.type === "system") {
    return (
      <span className="text-ink-subtle">{meta.reason ?? "systeem-actie"}</span>
    );
  }
  return null;
};

const CommentBody = ({
  activity,
  canEdit,
  onEdit,
  onDelete,
  editing,
  setEditingId,
}) => {
  const [draft, setDraft] = useState(activity.body ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const cancel = () => {
    setDraft(activity.body ?? "");
    setError(null);
    setEditingId(null);
  };
  const save = async () => {
    const trimmed = draft.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onEdit(activity._id, trimmed);
      setEditingId(null);
    } catch (err) {
      setError(
        err?.response?.data?.error ??
          err?.message ??
          "Bijwerken mislukt.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="mt-1">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          autoFocus
          className="w-full resize-none rounded border-2 border-brand bg-white px-2 py-1.5 text-sm text-ink placeholder:text-ink-subtlest focus:outline-none"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save();
            if (e.key === "Escape") cancel();
          }}
        />
        {error ? (
          <p className="mt-1 text-xs text-[#AE2A19]">{error}</p>
        ) : null}
        <div className="mt-1.5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="h-8 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Annuleer
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!draft.trim() || saving}
            className="h-8 rounded bg-brand px-3 text-sm font-medium text-white transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Opslaan…" : "Opslaan"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative mt-1 rounded bg-sunken px-3 py-2 pr-16 text-sm text-ink">
      <p className="whitespace-pre-wrap">{activity.body}</p>
      {canEdit ? (
        <div className="absolute right-1.5 top-1.5 flex items-center gap-1">
          <button
            type="button"
            onClick={() => setEditingId(activity._id)}
            title="Bewerken"
            className="flex h-6 w-6 items-center justify-center rounded text-ink-subtlest transition hover:bg-[#091E420F] hover:text-ink"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828zM2 17a1 1 0 011-1h14a1 1 0 110 2H3a1 1 0 01-1-1z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => onDelete(activity._id)}
            title="Verwijderen"
            className="flex h-6 w-6 items-center justify-center rounded text-ink-subtlest transition hover:bg-[#FFECEB] hover:text-[#AE2A19]"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      ) : null}
    </div>
  );
};

const ActivityItem = ({
  activity,
  currentUserId,
  onEditComment,
  onDeleteComment,
  editingId,
  setEditingId,
  statusesById,
  adminsById,
  currentUserPhoto,
}) => {
  const isComment = activity.type === "comment";
  const isSystem =
    activity.type === "system" || activity.actor?.naam === "Stripe";
  const isMine =
    isComment &&
    currentUserId &&
    String(activity.actor?.userId ?? "") === String(currentUserId);

  // Photo lookup: eigen userId → currentUserPhoto, anders admins-map → profielFoto.
  // Voor oude records zonder actor.userId: probeer match op naam.
  const actorId = String(activity.actor?.userId ?? "");
  const actorName = activity.actor?.naam ?? "";
  const adminByName = !actorId
    ? Object.values(adminsById ?? {}).find((a) => a?.naam === actorName)
    : null;
  const photoUrl = isMine
    ? currentUserPhoto
    : adminsById?.[actorId]?.profielFoto ?? adminByName?.profielFoto ?? null;

  return (
    <div className="relative flex gap-3 py-2">
      <div className="flex-none pt-0.5">
        <Avatar
          name={activity.actor?.naam ?? "?"}
          system={isSystem}
          photoUrl={photoUrl}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-xs leading-relaxed">
          <span className="font-medium text-ink">
            {activity.actor?.naam ?? "Onbekend"}
          </span>
          {!isComment ? (
            <ChangeLine activity={activity} statusesById={statusesById} />
          ) : (
            <span className="text-ink-subtlest">plaatste een opmerking</span>
          )}
          <span className="text-ink-subtlest" title={exactTime(activity.createdAt)}>
            · {relativeTime(activity.createdAt)}
          </span>
          {isComment && activity.isEdited ? (
            <span
              className="text-ink-subtlest"
              title={`Bijgewerkt ${exactTime(activity.updatedAt)}`}
            >
              · bijgewerkt {relativeTime(activity.updatedAt)}
            </span>
          ) : null}
        </div>
        {isComment && activity.body ? (
          <CommentBody
            activity={activity}
            canEdit={isMine}
            editing={editingId === activity._id}
            setEditingId={setEditingId}
            onEdit={onEditComment}
            onDelete={onDeleteComment}
          />
        ) : null}
      </div>
    </div>
  );
};

const groupByDay = (activities) => {
  const groups = [];
  let currentKey = null;
  for (const a of activities) {
    const key = a.createdAt ? startOfDay(a.createdAt) : 0;
    if (key !== currentKey) {
      currentKey = key;
      groups.push({ key, label: dayLabel(a.createdAt), items: [] });
    }
    groups[groups.length - 1].items.push(a);
  }
  return groups;
};

const BtwActivityFeed = ({
  activities = [],
  onAddComment,
  onEditComment,
  onDeleteComment,
  posting = false,
  currentUserId,
  currentUserPhoto,
  statuses = [],
  admins = [],
}) => {
  const [body, setBody] = useState("");
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const statusesById = useMemo(() => {
    const map = {};
    for (const s of statuses ?? []) {
      if (s?.name) map[s.name] = s;
    }
    return map;
  }, [statuses]);

  const adminsById = useMemo(() => {
    const map = {};
    for (const a of admins ?? []) {
      if (a?._id) map[String(a._id)] = a;
    }
    return map;
  }, [admins]);

  const groups = useMemo(() => groupByDay(activities), [activities]);

  const submit = async () => {
    const trimmed = body.trim();
    if (!trimmed || posting) return;
    setError(null);
    try {
      await onAddComment(trimmed);
      setBody("");
    } catch (err) {
      const msg =
        err?.response?.data?.error ??
        err?.message ??
        "Opmerking plaatsen mislukt. Probeer opnieuw.";
      setError(msg);
    }
  };

  return (
    <section className="mb-5 overflow-hidden rounded bg-white shadow-card">
      <header className="flex items-center justify-between border-b border-line px-5 py-3">
        <h3 className="text-base font-semibold text-ink">
          Activiteit &amp; opmerkingen
        </h3>
        <span className="text-xs text-ink-subtlest">
          {activities.length} item{activities.length === 1 ? "" : "s"}
        </span>
      </header>

      {/* Composer */}
      <div className="border-b border-line bg-sunken px-5 py-4">
        {error ? (
          <div className="mb-2 flex items-start justify-between gap-2 rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              className="text-[#AE2A19] hover:underline"
              aria-label="Sluit melding"
            >
              ✕
            </button>
          </div>
        ) : null}
        <textarea
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            if (error) setError(null);
          }}
          placeholder="Plaats een interne opmerking, alleen voor admins zichtbaar."
          rows={2}
          className="w-full resize-none rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink placeholder:text-ink-subtlest focus:border-brand focus:outline-none"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
          }}
        />
        <div className="mt-2 flex items-center justify-between">
          <span className="text-[11px] text-ink-subtlest">
            ⌘/Ctrl + Enter om te plaatsen
          </span>
          <button
            type="button"
            disabled={!body.trim() || posting}
            onClick={submit}
            className="h-8 rounded bg-brand px-3 text-sm font-medium text-white transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {posting ? "Plaatsen…" : "Plaats opmerking"}
          </button>
        </div>
      </div>

      {/* Timeline */}
      <div className="px-5 py-3">
        {activities.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-subtlest">
            Nog geen activiteit. Status-wijzigingen, toewijzingen en opmerkingen verschijnen hier.
          </p>
        ) : (
          groups.map((group) => (
            <div key={group.key} className="mb-3 last:mb-0">
              <div className="-mx-5 mb-1 border-b border-line bg-white px-5 py-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
                  {group.label}
                </span>
              </div>
              <ol className="relative ml-3 border-l border-line">
                {group.items.map((a, i) => (
                  <li key={a._id ?? i} className="pl-4">
                    <ActivityItem
                      activity={a}
                      currentUserId={currentUserId}
                      currentUserPhoto={currentUserPhoto}
                      onEditComment={onEditComment}
                      onDeleteComment={onDeleteComment}
                      editingId={editingId}
                      setEditingId={setEditingId}
                      statusesById={statusesById}
                      adminsById={adminsById}
                    />
                  </li>
                ))}
              </ol>
            </div>
          ))
        )}
      </div>
    </section>
  );
};

export default BtwActivityFeed;
